import { memo } from "react";
import type { GoldenSerenityComponentProps } from "./types";
import { GS, formatDate } from "./utils";
import { AmbientGlow, OrganicDivider } from "./decorations";

/**
 * Framed couple photo (event cover) with a warm caption. Renders nothing when
 * the event has no cover image.
 */
export const GoldenSerenityCouplePhoto = memo(({ event, config, locale, t }: GoldenSerenityComponentProps) => {
  if (!event.cover) return null;

  const localeCode = (locale || "ro") as "ro" | "en";
  const caption =
    event.starBride && event.starGroom
      ? `${event.starBride} & ${event.starGroom}`
      : event.starBaptism || event.name;
  const tagline =
    event.heroText?.[localeCode] || event.heroText?.ro || event.heroText?.en || t("events.form.template.foreverAlways");

  return (
    <section
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: `linear-gradient(to bottom, ${GS.creamDeep}, ${GS.creamLight})` }}
    >
      <AmbientGlow />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10 space-y-10 sm:space-y-14">
        {/* Natural frame with warm tones */}
        <div className="max-w-2xl mx-auto">
          <div
            className="relative p-2 rounded-sm"
            style={{
              background: `linear-gradient(to bottom right, ${GS.border}, ${GS.frame})`,
              boxShadow: "0 8px 40px rgba(139,115,85,0.15)",
            }}
          >
            <div
              className="p-4 sm:p-8 rounded-sm"
              style={{ background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.cream})` }}
            >
              <div className="relative aspect-[3/4] overflow-hidden rounded-sm">
                <img
                  src={event.cover}
                  alt={caption}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(to top, rgba(201,168,106,0.10), transparent)" }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Warm caption */}
        <div className="text-center space-y-6">
          <OrganicDivider />
          <p
            className="text-3xl sm:text-4xl font-medium"
            style={{ color: config.palette.text, fontFamily: config.typography.heading }}
          >
            {caption}
          </p>
          {event.date && (
            <p
              className="text-sm tracking-wide"
              style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
            >
              {formatDate(event.date, locale, { day: "numeric", month: "long", year: "numeric" })}
            </p>
          )}
          <p
            className="text-lg sm:text-xl italic max-w-md mx-auto pt-2"
            style={{ color: config.palette.mutedText, fontFamily: config.typography.heading }}
          >
            {tagline}
          </p>
        </div>
      </div>
    </section>
  );
});

GoldenSerenityCouplePhoto.displayName = "GoldenSerenityCouplePhoto";
